import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  InlineStack,
  Text,
  Icon,
  Badge,
  Button,
  Thumbnail,
  Divider,
  BlockStack,
  FormLayout,
  TextField,
  Select,
  DropZone,
  Toast,
  InlineError,
  Spinner,
} from "@shopify/polaris";
import { XIcon, EditIcon } from "@shopify/polaris-icons";
import { createPortal } from "react-dom";
import { MultiSelect } from "react-multi-select-component";
import axios from "../Assets/Lib/axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";

const TaskSlider = ({ task, closeSlider, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { id } = useParams();
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toggleToastActive = () => setToastActive((a) => !a);
  console.log("taskslider=", task);
  const [statusOptions, setStatusOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [peopleOptions, setPeopleOptions] = useState([]);

  const uploadedKeys = useRef([]);
  const [files, setFiles] = useState([]);
  const [existingScreenshots, setExistingScreenshots] = useState(
    task.screenshots.map((url, i) => ({
      url,
      key: task.screenshots_keys[i],
    })) || []
  );

  console.log("testtask=", task);
  const [editValues, setEditValues] = useState({
    title: task.title || "",
    is_high: String(task.is_high) || "0",
    description: task.description || "",
    status: task.status?.id ? String(task.status.id) : "",
    country: task.country || "",
    assignees:
      task.assignees?.map((a) => ({
        label: a.name,
        value: String(a.id),
      })) || [],
    reporters:
      task.reporters?.map((r) => ({
        label: r.name,
        value: String(r.id),
      })) || [],
    deadline: task.deadline || "",
    category: task.category?.id ? String(task.category.id) : "",
  });
  const [categoryOptions, setCategoryOptions] = useState([]);

  // ✅ Fetch dropdown options (statuses, countries, people)
  useEffect(() => {
    axios.get(`/task-manager-projects/${id}/categories`).then((res) => {
      const data = res?.data?.data?.data || [];
      setCategoryOptions(
        data.map((c) => ({
          label: c.title,
          value: String(c.id),
        }))
      );
    });
    axios.get(`/task-manager-projects/${id}/statuses`).then((res) => {
      const data = res?.data?.data?.data || [];
      setStatusOptions(
        data.map((s) => ({ label: s.name, value: String(s.id) }))
      );
    });

    axios.get("/settings/countries").then((res) => {
      const data = res?.data?.data || [];
      const options = [
        { label: "All", value: "All" }, // ✅ Add "All" option at the top
        ...data.map((country) => ({
          label: country.name,
          value: country.name,
        })),
      ];
      setCountryOptions(options);
    });

    axios.get("/people?all").then((res) => {
      const data = res?.data?.data || [];
      setPeopleOptions(
        data.map((p) => ({
          label: p.name,
          value: String(p.id),
        }))
      );
    });
  }, [task.project_id]);

  // ✅ handleChange
  const handleChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };
  const pastelColors = [
    "#FADADD", // pink
    "#F9E79F", // yellow
    "#A3E4D7", // teal
    "#AED6F1", // blue
    "#D7BDE2", // purple
    "#F5CBA7", // peach
    "#ABEBC6", // mint
    "#FAD7A0", // light orange
  ];

  // 🔹 Generate consistent color for each name
  function getColorForName(name) {
    if (!name) return pastelColors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % pastelColors.length;
    return pastelColors[index];
  }

  const bgColor = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * pastelColors.length);
    return pastelColors[randomIndex];
  }, []);

  // ✅ Upload screenshots (S3)
  const handleDropZone = useCallback(async (_dropFiles, acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setIsUploading(true);
    try {
      setFiles((prev) => [...prev, ...acceptedFiles]);
      const uploads = acceptedFiles.map((file) => {
        const form_data = new FormData();
        form_data.append("entity_type", "task_manager_screenshot");
        form_data.append("image", file);
        return axios.post("/s3/save-file", form_data);
      });
      const results = await Promise.all(uploads);
      const newKeys = results.map((r) => r.data.data.file_key);
      uploadedKeys.current = [...uploadedKeys.current, ...newKeys];
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  }, []);

  // ✅ Save task (PATCH)
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("_method", "PATCH");
      formData.append("title", editValues.title);
      formData.append("description", editValues.description);
      formData.append("status_id", editValues.status);
      formData.append("country", editValues.country);
      formData.append("deadline", editValues.deadline);
      formData.append("title", editValues.title);
      formData.append("is_high", Number(editValues.is_high));
      editValues.assignees.forEach((m, i) =>
        formData.append(`assignee_ids[${i}]`, m.value)
      );
      editValues.reporters.forEach((m, i) =>
        formData.append(`reporter_ids[${i}]`, m.value)
      );

      existingScreenshots.forEach((img, i) => {
        formData.append(`images[${i}]`, img.key);
      });
      formData.append("category_id", editValues.category);
      uploadedKeys.current.forEach((key, i) => {
        formData.append(`images[${existingScreenshots.length + i}]`, key);
      });

      await axios.post(`/task-manager/${task.id}`, formData);
      setToastMessage("Task updated successfully");
      setToastActive(true);
      setIsEditing(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to update task:", err);
      setToastMessage("Failed to update task");
      setToastActive(true);
    } finally {
      setIsSaving(false);
    }
  };

  function daysFromToday(dateString) {
    if (!dateString) return null; // no deadline
    const target = new Date(dateString);
    if (isNaN(target)) return null; // invalid date

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  }

  return createPortal(
    <>
      <div
        onClick={() => closeSlider()}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 999999,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "440px",
          background: "#fff",
          boxShadow: "-2px 0 8px rgba(0,0,0,0.15)",
          padding: "24px",
          zIndex: 1000000,
          overflowY: "auto",
          borderLeft: "1px solid #E1E3E5",
          pointerEvents: "auto",
        }}
      >
        <FormLayout>
          {/* HEADER */}
          <Box position="relative">
            {/* Top Row: Title and X */}
            <Box paddingBlockEnd="200">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start", // important: top-aligns right section
                  gap: "12px",
                }}
              >
                {/* Title Section */}
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  {isEditing ? (
                    <TextField
                      value={editValues.title}
                      onChange={(v) => handleChange("title", v)}
                      autoComplete="off"
                    />
                  ) : (
                    <Text
                      variant="headingMd"
                      fontWeight="semibold"
                      breakWord
                      as="h2"
                      style={{
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {task.title}
                    </Text>
                  )}
                </div>

                {/* Action Section */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    flexShrink: 0,
                  }}
                >
                  {/* Close icon */}
                  <div
                    style={{ cursor: "pointer", marginBottom: "4px" }}
                    onClick={() => closeSlider()}
                  >
                    <Icon source={XIcon} />
                  </div>

                  {/* Edit / Save / Cancel buttons */}
                  {!isEditing ? (
                    <Button
                      icon={EditIcon}
                      variant="plain"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <InlineStack gap="100">
                      <Button
                        variant="plain"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        tone="success"
                        onClick={handleSave}
                        loading={isSaving}
                      >
                        Save
                      </Button>
                    </InlineStack>
                  )}
                </div>
              </div>

              {/* <Divider borderColor="border" /> */}
            </Box>

            {/* <Divider borderColor="border" /> */}
          </Box>
          {/* <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" fontWeight="semibold">
              {task.title}
            </Text>
            <InlineStack gap="200" blockAlign="center">
              {!isEditing ? (
                <Button
                  icon={EditIcon}
                  variant="plain"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button variant="plain" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button
                    tone="success"
                    onClick={handleSave}
                    loading={isSaving}
                  >
                    Save
                  </Button>
                </>
              )}
              <div style={{ cursor: "pointer" }} onClick={() => closeSlider()}>
                <Icon source={XIcon} />
              </div>
            </InlineStack>
          </InlineStack> */}

          <Divider borderColor="border" />

          {/* Description */}
          <Text variant="headingSm" fontWeight="medium">
            Description
          </Text>
          {isEditing ? (
            <TextField
              value={editValues.description}
              onChange={(v) => handleChange("description", v)}
              multiline
            />
          ) : (
            <Text color="subdued">
              {task.description || "No description provided"}
            </Text>
          )}

          <Divider borderColor="border" />

          {/* Status / Country / Deadline */}
          <InlineStack gap="400" blockAlign="center">
            <BlockStack>
              <Text variant="headingSm" fontWeight="medium">
                Status
              </Text>
              {isEditing ? (
                <Badge tone="attention">{task.status?.name || "Backlog"}</Badge>
              ) : (
                // <Select
                //   options={statusOptions}
                //   value={editValues.status}
                //   onChange={(v) => handleChange("status", v)}
                //   placeholder="Select status"
                // />
                <Badge tone="attention">{task.status?.name || "Backlog"}</Badge>
              )}
            </BlockStack>

            <BlockStack>
              <Text variant="headingSm" fontWeight="medium">
                Country
              </Text>
              {isEditing ? (
                <Select
                  options={countryOptions}
                  value={editValues.country}
                  onChange={(v) => handleChange("country", v)}
                  placeholder="Select country"
                />
              ) : (
                <Text>{task.country || "—"}</Text>
              )}
            </BlockStack>
          </InlineStack>

          <Divider borderColor="border" />

          {/* Deadline */}
          <Text variant="headingSm" fontWeight="medium">
            Deadline
          </Text>
          {isEditing ? (
            <TextField
              type="date"
              value={editValues.deadline}
              onChange={(v) => handleChange("deadline", v)}
            />
          ) : task.deadline ? (
            <Text>
              {task.deadline}{" "}
              {daysFromToday(task.deadline) !== null && (
                <Text
                // tone={
                //   daysFromToday(task.deadline) < 0
                //     ? "critical"
                //     : daysFromToday(task.deadline) <= 3
                //     ? "attention"
                //     : "success"
                // }
                >
                  {daysFromToday(task.deadline) < 0
                    ? `(${Math.abs(daysFromToday(task.deadline))} days overdue)`
                    : `(${daysFromToday(task.deadline)} days left)`}
                </Text>
              )}
            </Text>
          ) : (
            <Text color="subdued">NA</Text>
          )}
          <Divider borderColor="border" />
          <Text variant="headingSm" fontWeight="medium">
            Category
          </Text>
          {isEditing ? (
            <Select
              options={categoryOptions}
              value={editValues.category}
              onChange={(v) => handleChange("category", v)}
              placeholder="Select category"
            />
          ) : (
            <Text>{task.category?.title || "—"}</Text>
          )}

          <Divider borderColor="border" />
          <Text variant="headingSm" fontWeight="medium">
            Is high priority?
          </Text>
          {isEditing ? (
            <Select
              options={[
                { label: "Yes", value: "1" },
                { label: "No", value: "0" },
              ]}
              value={editValues.is_high}
              onChange={(v) => handleChange("is_high", v)}
              placeholder="Select option"
            />
          ) : (
            <Text>{String(task.is_high) === "1" ? "Yes" : "No"}</Text>
          )}

          <Divider borderColor="border" />

          {/* Assignees */}
          <Text variant="headingSm" fontWeight="medium">
            Assignees
          </Text>
          {isEditing ? (
            <div style={{ position: "relative", zIndex: 9999 }}>
              <MultiSelect
                options={peopleOptions}
                value={editValues.assignees}
                onChange={(v) => handleChange("assignees", v)}
                labelledBy="Select Assignees"
              />
            </div>
          ) : (
            <BlockStack marginBlockStart="200" gap="200">
              {task.assignees?.map((m, i) => (
                <InlineStack key={i} gap="200" blockAlign="center">
                  <img
                    src={m.image_url}
                    alt={m.name}
                    width={36}
                    height={36}
                    style={{ borderRadius: "50%" }}
                  />
                  <Text>{m.name}</Text>
                </InlineStack>
              ))}
            </BlockStack>
          )}

          <Divider borderColor="border" />

          {/* Reporters */}
          <Text variant="headingSm" fontWeight="medium">
            Reporters
          </Text>
          {isEditing ? (
            <div style={{ position: "relative", zIndex: 9999 }}>
              <MultiSelect
                options={peopleOptions}
                value={editValues.reporters}
                onChange={(v) => handleChange("reporters", v)}
                labelledBy="Select Reporters"
              />
            </div>
          ) : (
            <BlockStack marginBlockStart="200" gap="200">
              {task.reporters?.map((m, i) => (
                <InlineStack key={i} gap="200" blockAlign="center">
                  <div
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      // border: `2px solid  "#f0f2f4"`,
                      userSelect: "none",
                      pointerEvents: "auto",
                      cursor: "pointer",
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: getColorForName(m.name),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#333",
                      // fontWeight: "600",
                      fontSize: "0.6rem",
                      textTransform: "uppercase",
                      userSelect: "none",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    }}
                  >
                    {m?.name
                      ?.split(" ")
                      .filter(Boolean)
                      .map((word) => word[0].toUpperCase())
                      .slice(0, 2)
                      .join("")}
                  </div>
                  <Text>{m.name}</Text>
                </InlineStack>
              ))}
            </BlockStack>
          )}

          <Divider borderColor="border" />

          {/* Screenshots */}
          <Text variant="headingSm" fontWeight="medium">
            Screenshots
          </Text>
          {isEditing ? (
            <>
              <DropZone onDrop={handleDropZone} allowMultiple accept="image/*">
                <DropZone.FileUpload />
              </DropZone>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                {existingScreenshots.map((img, index) => (
                  <div
                    key={`existing-${index}`}
                    style={{ position: "relative" }}
                  >
                    <Thumbnail source={img.url} size="small" />
                    <Button
                      tone="critical"
                      size="slim"
                      onClick={() =>
                        setExistingScreenshots((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        borderRadius: "50%",
                        minWidth: "24px",
                        height: "24px",
                      }}
                    >
                      ×
                    </Button>
                  </div>
                ))}
                {files.map((file, index) => (
                  <div
                    key={`new-${index}`}
                    style={{ position: "relative", width: 100 }}
                  >
                    <Thumbnail
                      source={URL.createObjectURL(file)}
                      size="small"
                    />
                    <Button
                      tone="critical"
                      size="slim"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, i) => i !== index))
                      }
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        borderRadius: "50%",
                        minWidth: "24px",
                        height: "24px",
                      }}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <InlineStack gap="300" marginBlockStart="200">
              {task.screenshots?.map((img, i) => (
                <div key={i} onClick={() => window.open(img, "_blank")}>
                  <Thumbnail source={img} size="large" />
                </div>
              ))}
            </InlineStack>
          )}

          <Divider borderColor="border" />

          {/* Delete Action */}
          {/* <Button tone="critical" fullWidth>
            Delete
          </Button> */}
        </FormLayout>
      </div>

      {toastActive && (
        <Toast content={toastMessage} onDismiss={toggleToastActive} />
      )}
      {isUploading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            right: "50%",
            transform: "translate(50%, -50%)",
          }}
        >
          <Spinner size="large" />
        </div>
      )}
    </>,
    document.body
  );
};

export default TaskSlider;
